import { FormHelperText, makeStyles, TextField } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { separarCidadeUf } from "./camposAtualizacaoCadastral";

interface Props {
  id: string;
  value: string;
  onChange: (valor: string) => void;
  onBlur: () => void;
  erro: string;
  disabled: boolean;
  required: boolean;
}

const useStyles = makeStyles((theme) => ({
  linha: {
    display: "flex",
  },
  uf: {
    flex: "0 0 72px",
    marginLeft: theme.spacing(2),
  },
}));

const juntar = (cidade: string, uf: string) => (cidade || uf ? `${cidade} - ${uf}` : "");

/** Mantem so letras, em maiusculas: a UF e a sigla do estado. */
const formatarUf = (valor: string) =>
  valor
    .replace(/[^A-Za-z]/g, "")
    .toUpperCase()
    .slice(0, 2);

/**
 * Cidade e UF digitadas (ou preenchidas pela busca do CEP) em dois inputs, mas um unico valor
 * no formulario ("NOME - UF"), que o servidor separa em PESSOA.CIDADE e PESSOA.ESTADO.
 */
const CampoCidade = ({ id, value, onChange, onBlur, erro, disabled, required }: Props) => {
  const classes = useStyles();
  // Os inputs guardam o texto como digitado: refazer a separacao a cada tecla comeria o espaco
  // ao fim da cidade ("Belo |") e impediria digitar nomes compostos.
  const [partes, setPartes] = useState(() => separarCidadeUf(value));

  // Valor trocado fora daqui (busca do CEP): passa a valer o que veio.
  useEffect(() => {
    if (value !== juntar(partes.cidade, partes.uf)) {
      setPartes(separarCidadeUf(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const alterar = (cidade: string, uf: string) => {
    setPartes({ cidade, uf });
    onChange(juntar(cidade, uf));
  };

  const idUf = `${id}-UF`;
  // Sem a cidade preenchida o erro e da cidade; com ela, so pode ser da UF.
  const erroNaCidade = !!erro && partes.cidade.trim().length === 0;
  return (
    <div>
      <div className={classes.linha}>
        <TextField
          id={id}
          label="Cidade"
          value={partes.cidade}
          onChange={(e) => alterar(e.target.value, partes.uf)}
          // Ir da cidade para a UF ainda nao conta como campo visitado, senao o erro "Informe a UF" aparece antes da hora.
          onBlur={(e) => {
            if (!e.relatedTarget || (e.relatedTarget as HTMLElement).id !== idUf) {
              onBlur();
            }
          }}
          error={erroNaCidade}
          autoComplete="address-level2"
          inputProps={{ maxLength: 50, "aria-describedby": `${id}-helper-text` }}
          InputLabelProps={{ shrink: true }}
          disabled={disabled}
          required={required}
          fullWidth
        />
        <TextField
          id={idUf}
          label="UF"
          className={classes.uf}
          value={partes.uf}
          onChange={(e) => alterar(partes.cidade, formatarUf(e.target.value))}
          onBlur={onBlur}
          error={!!erro && !erroNaCidade}
          autoComplete="address-level1"
          inputProps={{ maxLength: 2, "aria-describedby": `${id}-helper-text` }}
          InputLabelProps={{ shrink: true }}
          disabled={disabled}
          required={required}
        />
      </div>
      {erro && (
        <FormHelperText id={`${id}-helper-text`} error>
          {erro}
        </FormHelperText>
      )}
    </div>
  );
};

export default CampoCidade;
